import React, { useState, useEffect } from 'react';
import {
  HiOutlineDeviceMobile, HiOutlineShare, HiPlus, HiOutlineBell,
  HiOutlineLightningBolt, HiOutlineChatAlt2,
} from 'react-icons/hi';
import notificationService from '../services/notificationService';
import './PWAInstallPrompt.css';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DAYS = 7;
// Show a little sooner than before so engaged users see it while active.
const SHOW_DELAY_MS = 15000;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  // view: null | 'banner' | 'details' | 'ios' | 'notifications'
  const [view, setView] = useState(null);
  const [enablingNotifications, setEnablingNotifications] = useState(false);

  useEffect(() => {
    // Testing/preview hook: ?previewInstall=1 forces the banner to render on
    // any device (bypasses the mobile / installed / dismissed gates) so the
    // prompt can be viewed and styled without a real install context.
    // ?previewInstall=notifications previews the notifications prompt instead.
    const previewParam = new URLSearchParams(window.location.search).get('previewInstall');
    if (previewParam) {
      setView(previewParam === 'notifications' ? 'notifications' : 'banner');
      return;
    }

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) return;

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    const installed = isStandalone();

    // Respect a recent dismissal.
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const days = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
      if (days < DISMISS_DAYS) return;
    }

    // Case 1: Already installed but notifications are off -> nudge notifications only.
    if (installed) {
      if (
        notificationService.isNotificationSupported() &&
        notificationService.getPermissionStatus() === 'default'
      ) {
        const t = setTimeout(() => setView('notifications'), SHOW_DELAY_MS);
        return () => clearTimeout(t);
      }
      return; // installed + notifications handled: nothing to do
    }

    // Case 2: Not installed. iOS can't use beforeinstallprompt, so show after a delay.
    let timer;
    if (iOS) {
      timer = setTimeout(() => setView('banner'), SHOW_DELAY_MS);
    }

    // Android/Chromium: capture the install event and show the banner.
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setView('banner');
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setView('ios');
      return;
    }

    if (!deferredPrompt) {
      // No native prompt available; show the manual details card.
      setView('details');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);

    if (outcome === 'accepted') {
      // After install, ask them to turn on notifications for the full experience.
      if (
        notificationService.isNotificationSupported() &&
        notificationService.getPermissionStatus() === 'default'
      ) {
        setView('notifications');
      } else {
        setView(null);
      }
    } else {
      setView(null);
    }
  };

  const handleEnableNotifications = async () => {
    setEnablingNotifications(true);
    try {
      await notificationService.requestPermission();
    } catch (e) {
      // Permission failures are non-critical; just close the prompt.
    } finally {
      setEnablingNotifications(false);
      setView(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setView(null);
  };

  if (!view) return null;

  // Notifications-only prompt (already installed, or just installed).
  if (view === 'notifications') {
    return (
      <div className="pwa-overlay" role="dialog" aria-modal="true" aria-labelledby="pwa-notif-title">
        <div className="pwa-modal">
          <button className="pwa-close" onClick={handleDismiss} aria-label="Close">×</button>
          <div className="pwa-icon" aria-hidden="true"><HiOutlineBell /></div>
          <h3 id="pwa-notif-title">Turn on notifications</h3>
          <p className="pwa-modal-sub">
            Get instant alerts for new messages, matches and calls so you never miss a connection.
          </p>
          <button
            className="pwa-button-primary"
            onClick={handleEnableNotifications}
            disabled={enablingNotifications}
          >
            {enablingNotifications ? 'Enabling…' : 'Enable Notifications'}
          </button>
          <button className="pwa-button-secondary" onClick={handleDismiss}>
            Maybe later
          </button>
        </div>
      </div>
    );
  }

  // iOS add-to-home-screen instructions.
  if (view === 'ios') {
    return (
      <div className="pwa-overlay" role="dialog" aria-modal="true" aria-labelledby="pwa-ios-title">
        <div className="pwa-modal">
          <button className="pwa-close" onClick={handleDismiss} aria-label="Close">×</button>
          <div className="pwa-icon" aria-hidden="true"><HiOutlineDeviceMobile /></div>
          <h3 id="pwa-ios-title">Install KwikLuv</h3>
          <div className="ios-instructions">
            <p>Add KwikLuv to your home screen for the full app experience:</p>
            <ol>
              <li>Tap the <strong>Share</strong> button <HiOutlineShare aria-hidden="true" style={{ fontSize: '20px', verticalAlign: 'middle' }} /> at the bottom of Safari</li>
              <li>Scroll down and tap <strong>"Add to Home Screen"</strong> <HiPlus aria-hidden="true" style={{ fontSize: '20px', verticalAlign: 'middle' }} /></li>
              <li>Tap <strong>"Add"</strong> in the top right corner</li>
            </ol>
            <p className="pwa-ios-note">
              Once added, open KwikLuv from your home screen and allow notifications to never miss a message.
            </p>
          </div>
          <button className="pwa-button-secondary" onClick={handleDismiss}>
            Got it
          </button>
        </div>
      </div>
    );
  }

  // Benefit-led details card (Android fallback / "learn more").
  if (view === 'details') {
    return (
      <div className="pwa-overlay" role="dialog" aria-modal="true" aria-labelledby="pwa-details-title">
        <div className="pwa-modal">
          <button className="pwa-close" onClick={handleDismiss} aria-label="Close">×</button>
          <div className="pwa-icon" aria-hidden="true"><HiOutlineDeviceMobile /></div>
          <h3 id="pwa-details-title">Get the best experience</h3>
          <p className="pwa-modal-sub">Install KwikLuv on your phone to unlock:</p>
          <ul className="pwa-benefits">
            <li><span className="pwa-benefit-icon" aria-hidden="true"><HiOutlineBell /></span> Instant alerts for messages, matches &amp; calls</li>
            <li><span className="pwa-benefit-icon" aria-hidden="true"><HiOutlineLightningBolt /></span> Faster, full-screen app feel</li>
            <li><span className="pwa-benefit-icon" aria-hidden="true"><HiOutlineChatAlt2 /></span> One tap from your home screen</li>
          </ul>
          <button className="pwa-button-primary" onClick={handleInstall}>
            Install App
          </button>
          <button className="pwa-button-secondary" onClick={handleDismiss}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  // Default: compact benefit-led banner.
  return (
    <div className="pwa-banner" role="region" aria-label="Install app">
      <div className="pwa-content">
        <div className="pwa-icon-small" aria-hidden="true"><HiOutlineDeviceMobile /></div>
        <div className="pwa-text">
          <strong>Get the best experience</strong>
          <p>Install the app &amp; turn on alerts so you never miss a message.</p>
        </div>
      </div>
      <div className="pwa-actions">
        <button className="pwa-button" onClick={handleInstall}>
          Install
        </button>
        <button className="pwa-dismiss" onClick={handleDismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
